// Mirrors the checks in register() in web3pwn-api handlers/user.py. Keep the
// two in step: a rule that only the backend enforces surfaces as a single
// form-level message instead of an error on the field that caused it.

export interface RegistrationValues {
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
    acceptTerms: boolean;
}

export type RegistrationField = keyof RegistrationValues;
export type RegistrationErrors = Partial<Record<RegistrationField, string>>;

export const USERNAME_REGEX = /^[A-Za-z0-9_]{3,20}$/;
// Same shape as validate_email on the backend, including its 2-7 letter TLD.
export const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,7}$/;
export const PASSWORD_MIN = 4;
export const PASSWORD_MAX = 64;

export const USERNAME_HINT = "3-20 characters: letters, numbers and underscores";
export const PASSWORD_HINT = `${PASSWORD_MIN}-${PASSWORD_MAX} characters`;

// Field order matches the form, so the first key is the first invalid field.
export const validateRegistration = (values: RegistrationValues): RegistrationErrors => {
    const errors: RegistrationErrors = {};

    if (!values.username) {
        errors.username = "Enter a username";
    } else if (!USERNAME_REGEX.test(values.username)) {
        errors.username = `Username must be ${USERNAME_HINT}`;
    }

    if (!values.email) {
        errors.email = "Enter your email address";
    } else if (!EMAIL_REGEX.test(values.email)) {
        errors.email = "Enter a valid email address, like name@example.com";
    }

    // Counted in code points, as Python's len() does on the backend.
    const passwordLength = Array.from(values.password).length;
    if (!values.password) {
        errors.password = "Enter a password";
    } else if (passwordLength < PASSWORD_MIN) {
        errors.password = `Password must be at least ${PASSWORD_MIN} characters`;
    } else if (passwordLength > PASSWORD_MAX) {
        errors.password = `Password must be at most ${PASSWORD_MAX} characters`;
    }

    if (!values.confirmPassword) {
        errors.confirmPassword = "Confirm your password";
    } else if (values.confirmPassword !== values.password) {
        errors.confirmPassword = "Passwords do not match";
    }

    if (!values.acceptTerms) {
        errors.acceptTerms = "You need to accept the Terms and Conditions and Privacy Policy to register";
    }

    return errors;
};
